import React, { Component, FormEvent } from 'react';
import Editor from '@monaco-editor/react';
import { RootState } from '../../../redux/store';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { FormicaProps, withFormica } from '../../../helpers/withFormica';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../../constant/CollectionNames';
import { requireNonUndefined } from '../../../util/Assert';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import FormicaLabel from '../../custom/FormicaLabel';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../../custom/FormicaSearchSelectBox';
import { TransactionJsonModel } from '../../../domain/stream/transaction/TransactionDomains';
import CustomLoading from '../../custom/CustomLoading';
import RoleHelper from '../../../RoleHelper';
import { ActionState } from '../../../redux/reducers/ActionReducer';
import { changeAction, changeActionIsUpdate, clearAction } from '../../../redux/actions/ActionActions';
import { enterComponent, leaveComponent } from '../../../redux/actions/ComponentActions';
import { JiraActionJsonModel } from '../../../domain/collection/rule/action/ActionDomains';
import FormicaInput from '../../custom/FormicaInput';
import CustomPasswordInput from '../../custom/CustomPasswordInput';
import LanguageUtil from '../../../util/LanguageUtil';
import ComponentName from '../../../constant/ComponentName';

class ActionJiraEditorComponent extends Component<Props, State> {
  public state = new State();

  private transactionSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  public render() {
    const {
      action: { action },
      component: { module },
      user,
    } = this.props;
    const { loading } = this.state;
    const isKyc = module.includes('kyc');

    if (loading) {
      return <CustomLoading />;
    }

    return (
      <div className={'grid grid-cols-8 w-full !max-h-[calc(100vh-15rem)] overflow-y-hidden pb-8'}>
        <div className={'col-span-2  overflow-scroll flex flex-col max-h-[calc(100dvh-280px)] border-r border-f-black-5 px-5 py-5 gap-y-5'}>
          <div className={'flex flex-col gap-y-3'}>
            <span className={'text-primary font-bold text-md'}>
              {LanguageUtil.get({ id: 'pages.action.jira.title', defaultMessage: 'INTEGRATION CONFIGURATION' })}
            </span>
            <span className={'flex bg-[#F7F8FA] rounded-[4px] w-full justify-between gap-x-1 px-1 py-1 h-[36px]'}>
              <div
                onClick={() => this.onSelectType('transaction')}
                className={`${action.type == 'transaction' ? 'bg-white opacity-100' : 'opacity-50'} w-full cursor-pointer text-f-black-1 px-1 py-1 rounded-[2px] flex items-center justify-center`}
              >
                {LanguageUtil.get({ id: 'common.label.transaction', defaultMessage: 'Transaction' })}
              </div>
              {isKyc && user.activeRiskType == 'compliance' && (
                <div
                  onClick={() => this.onSelectType('kyc')}
                  className={`${action.type == 'kyc' ? 'bg-white opacity-100' : 'opacity-50'} w-full cursor-pointer text-f-black-1 px-1 py-1 rounded-[2px] flex items-center justify-center`}
                >
                  {LanguageUtil.get({ id: 'common.label.kyc', defaultMessage: 'KYC' })}
                </div>
              )}
            </span>
            <div className={'flex flex-col w-full'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.channel', defaultMessage: 'Channel' })} />
              <FormicaSearchSelectBox
                options={this.getTransactionOptions()}
                rounded
                placeholder={LanguageUtil.get({ id: 'common.label.chanel', defaultMessage: 'Channel' })}
                disabled={!RoleHelper.canEdit('Action')}
                value={action.transaction}
                onChange={(e: FormicaSelectBoxItem) => this.onChangeTransaction(e)}
                data-testid={`${ComponentName.ACTION}_channel_select`}
              />
            </div>
            <div className={'w-full'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.timeout', defaultMessage: 'Timeout (ms)' })} />
              <FormicaInput
                disabled={!RoleHelper.canEdit('Action')}
                type={'number'}
                onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeTimeout(e)}
                value={action.timeout}
                placeholder={LanguageUtil.get({ id: 'common.label.timeout', defaultMessage: 'Timeout (ms)' })}
                data-testid={`${ComponentName.ACTION}_timeout_input`}
              />
            </div>
          </div>
          <div className={'flex flex-col gap-y-3'}>
            <span className={'text-primary font-bold text-md'}>
              {LanguageUtil.get({ id: 'pages.action.jira.jiraConfiguration', defaultMessage: 'JIRA CONFIGURATION' })}
            </span>
            <div className={'w-full'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.tenant', defaultMessage: 'Tenant' })} />
              <FormicaInput
                disabled={!RoleHelper.canEdit('Action')}
                onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeTenant(e)}
                value={(action as JiraActionJsonModel).tenant}
                placeholder={LanguageUtil.get({ id: 'common.input.placeholder.tenant', defaultMessage: 'Tenant' })}
                data-testid={`${ComponentName.ACTION}_tenant_input`}
              />
            </div>
            <div className={'w-full'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.username', defaultMessage: 'Username' })} />
              <FormicaInput
                disabled={!RoleHelper.canEdit('Action')}
                onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeUsername(e)}
                value={(action as JiraActionJsonModel).username}
                placeholder={LanguageUtil.get({ id: 'common.label.username', defaultMessage: 'Username' })}
                data-testid={`${ComponentName.ACTION}_username_input`}
              />
            </div>
            <div className={'w-full'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.password', defaultMessage: 'Password' })} />
              <CustomPasswordInput
                disabled={!RoleHelper.canEdit('Action')}
                onChange={(e: FormEvent<HTMLInputElement>) => this.onChangePassword(e)}
                value={(action as JiraActionJsonModel).password}
                placeholder={LanguageUtil.get({ id: 'common.label.password', defaultMessage: 'Password' })}
                data-testid={`${ComponentName.ACTION}_password_input`}
              />
            </div>
          </div>
          <div className={'flex flex-col gap-y-3'}>
            <span className={'text-primary font-bold text-md'}>
              {LanguageUtil.get({ id: 'pages.action.jira.issueTitle', defaultMessage: 'ISSUE CONFIGURATION' })}
            </span>
            <div className={'w-full'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.projectKey', defaultMessage: 'Project Key' })} />
              <FormicaInput
                disabled={!RoleHelper.canEdit('Action')}
                onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeProjectKey(e)}
                value={(action as JiraActionJsonModel).projectKey}
                placeholder={LanguageUtil.get({ id: 'common.label.projectKey', defaultMessage: 'projectKey' })}
                data-testid={`${ComponentName.ACTION}_project_key_input`}
              />
            </div>
            <div className={'w-full'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.issueType', defaultMessage: 'Issue Type' })} />
              <FormicaInput
                disabled={!RoleHelper.canEdit('Action')}
                onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeIssueType(e)}
                value={(action as JiraActionJsonModel).issueType}
                placeholder={LanguageUtil.get({ id: 'common.input.placeholder.issueType', defaultMessage: 'Issue Type' })}
                data-testid={`${ComponentName.ACTION}_issue_type_input`}
              />
            </div>
            <div className={'w-full'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.reporterId', defaultMessage: 'Reporter ID' })} />
              <FormicaInput
                disabled={!RoleHelper.canEdit('Action')}
                onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeReporterId(e)}
                value={(action as JiraActionJsonModel).reporterId}
                placeholder={LanguageUtil.get({ id: 'common.input.placeholder.reporterId', defaultMessage: 'Reporter ID' })}
                data-testid={`${ComponentName.ACTION}_reporter_id_input`}
              />
            </div>
            <div className={'w-full'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.parentId', defaultMessage: 'parrentId' })} />
              <FormicaInput
                disabled={!RoleHelper.canEdit('Action')}
                onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeParentId(e)}
                value={(action as JiraActionJsonModel).parentId}
                placeholder={LanguageUtil.get({ id: 'common.input.placeholder.parentId', defaultMessage: 'parentId' })}
                data-testid={`${ComponentName.ACTION}_parent_id_input`}
              />
            </div>
          </div>
        </div>
        <div className={'col-span-6 flex flex-col w-full h-full px-5 py-5 gap-y-2'}>
          <FormicaLabel label={LanguageUtil.get({ id: 'common.label.summary', defaultMessage: 'Summary' })} />
          <FormicaInput
            disabled={!RoleHelper.canEdit('Action')}
            onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeSummary(e)}
            value={(action as JiraActionJsonModel).summary}
            placeholder={LanguageUtil.get({ id: 'common.input.placeholder', defaultMessage: 'Summary' })}
            data-testid={`${ComponentName.ACTION}_summary_input`}
          />
          <FormicaLabel label={LanguageUtil.get({ id: 'common.label.description', defaultMessage: 'Description' })} />
          <Editor
            options={{ readOnly: !RoleHelper.canEdit('Action') }}
            className={'!border-[2px] rounded-[20px] py-4'}
            value={(action as JiraActionJsonModel).issueDescription}
            onChange={(e: string | undefined) => this.onChangeDescription(e)}
            language={'plaintext'}
            data-testid={`${ComponentName.ACTION}_issue_description_input`}
          />
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    await this.startTracker();
  }

  public async componentWillUnmount() {
    await this.stopTracker();
  }

  private startTracker = async () => {
    const transactionQuery = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS);
    this.transactionSubscription = subscribe.Formica.subscribe(CollectionNames.TRANSACTIONS, transactionQuery.build());
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.transactionSubscription]);
  };

  private tracker = async () => {
    if (this.transactionSubscription?.ready()) {
      const transactionQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).build();
      const transactions: TransactionJsonModel[] = await this.repository.findMany(transactionQuery);
      this.setState({ transactions, loading: false });
    }
  };

  private stopTracker = async () => {
    await this.transactionSubscription?.stop();
    this.computation?.stop();
  };

  private getTransactionOptions() {
    const {
      action: { action },
    } = this.props;
    if (action.type == 'kyc') {
      return [
        {
          label: LanguageUtil.get({ id: 'common.label.individual', defaultMessage: 'Individual' }),
          value: 'individual',
        },
        {
          label: LanguageUtil.get({ id: 'common.label.entity', defaultMessage: 'Entity' }),
          value: 'entity',
        },
      ];
    } else {
      return this.state.transactions.map((transaction) => {
        return {
          label: transaction.name,
          value: transaction.name,
        };
      });
    }
  }

  private onChangeTransaction(e: FormicaSelectBoxItem) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    action.transaction = e.value;
    changeAction(action);
  }

  private onChangeTimeout(e: FormEvent<HTMLInputElement>) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    action.timeout = parseInt(e.currentTarget.value);
    changeAction(action);
  }

  private onChangeUsername(e: FormEvent<HTMLInputElement>) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as JiraActionJsonModel).username = e.currentTarget.value;
    changeAction(action);
  }

  private onChangePassword(e: FormEvent<HTMLInputElement>) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as JiraActionJsonModel).password = e.currentTarget.value;
    changeAction(action);
  }

  private onChangeTenant(e: FormEvent<HTMLInputElement>) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as JiraActionJsonModel).tenant = e.currentTarget.value;
    changeAction(action);
  }

  private onChangeProjectKey(e: FormEvent<HTMLInputElement>) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as JiraActionJsonModel).projectKey = e.currentTarget.value;
    changeAction(action);
  }

  private onChangeIssueType(e: FormEvent<HTMLInputElement>) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as JiraActionJsonModel).issueType = e.currentTarget.value;
    changeAction(action);
  }

  private onChangeReporterId(e: FormEvent<HTMLInputElement>) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as JiraActionJsonModel).reporterId = e.currentTarget.value;
    changeAction(action);
  }

  private onChangeParentId(e: FormEvent<HTMLInputElement>) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as JiraActionJsonModel).parentId = e.currentTarget.value;
    changeAction(action);
  }

  private onChangeSummary(e: FormEvent<HTMLInputElement>) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as JiraActionJsonModel).summary = e.currentTarget.value;
    changeAction(action);
  }

  private onChangeDescription(e: string | undefined) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as JiraActionJsonModel).issueDescription = e || '';
    changeAction(action);
  }

  private onSelectType(type: string) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    action.type = type;
    changeAction(action);
  }
}

interface Props extends ReduxProps, FormicaProps {}

class State {
  transactions: TransactionJsonModel[] = [];
  loading: boolean = true;
}

const mapStateToProps = (state: RootState) => ({
  action: state.action as ActionState,
  isUpdate: state.action.isUpdate,
  lastCategory: state.page.lastCategory,
  component: state.component,
  user: state.auth.user,
});
const mapDispatchToProps = {
  changeActionIsUpdate,

  changeAction,
  clearAction,
  enterComponent,
  leaveComponent,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(ActionJiraEditorComponent));
